import type { Application } from '@labeling/globals';
import type {
  ApplicationAccess,
  FormAccessGroup,
  KnownOperator,
  OperatorAccessEntry,
} from './types';

export const GROUP_NAME_REGEX = /^[a-z0-9_-]{1,64}$/;

export const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const initials = (label: string) =>
  label
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('') || '?';

export const operatorDisplayName = (operator: KnownOperator | OperatorAccessEntry) =>
  operator.displayName ??
  operator.email ??
  operator.oid ??
  `#${'operatorId' in operator ? operator.operatorId : operator.id}`;

export const matchesOperator = (operator: KnownOperator, query: string) =>
  [operator.displayName, operator.email, operator.oid]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(query.toLowerCase());

export const projectApplicationAccess = (
  manageableApplications: Application[],
  assignments: { items: OperatorAccessEntry[] } | undefined,
  allKnownOperators: { items: KnownOperator[] } | undefined,
  groupsByApp: Partial<Record<Application, FormAccessGroup[]>> | undefined
): ApplicationAccess[] => {
  return manageableApplications.map((application) => {
    const appGroups = groupsByApp?.[application] ?? [];
    const nameById = new Map(appGroups.map((group) => [group.id, group.groupName]));
    const groups = new Map<string, OperatorAccessEntry[]>(
      appGroups.map((group) => [group.groupName, []])
    );
    const appAssignments = (assignments?.items ?? []).filter(
      (item) => item.application === application
    );

    // Assignments name their group by id only; the name comes from the application's group list.
    // One whose group is not in the list (not loaded, or failed to load) is not shown under a group,
    // but its operator still counts as assigned so they never appear unrestricted.
    for (const item of appAssignments) {
      const groupName = nameById.get(item.groupId);
      if (groupName !== undefined) groups.get(groupName)?.push(item);
    }

    const assignedIds = new Set(appAssignments.map((item) => item.operatorId));
    const unrestricted = (allKnownOperators?.items ?? []).filter(
      (operator) =>
        operator.operatorApplications.includes(application) && !assignedIds.has(operator.id)
    );

    return { application, groups, unrestricted };
  });
};

export const groupsForOperator = (
  assignments: { items: OperatorAccessEntry[] } | undefined,
  operatorId: number,
  application: Application
) =>
  (assignments?.items ?? [])
    .filter((a) => a.operatorId === operatorId && a.application === application)
    .map((a) => a.groupId);
